import { useMemo } from 'react';

import type { ApplicationContextElement } from '@opengov/app-core/navigation/useApplicationContext';

import { ldFlags } from '../../../application/common/flags';
import { useFeatureFlag } from '../../../application/providers/FeatureFlagsProvider';

export const REPORTING_ERP_MODE_CONTEXT_KEY = 'reporting_erp_mode';

export type ReportingErpMode = 'erp' | 'non-erp';

/**
 * BUD-20265: OG Assist application-context element telling the agent which reporting mode the
 * user is in, so it can pick the matching skills and tools for both report types (account
 * summary / multi-hierarchy and chart & details).
 *
 * The value comes from the `bnp-reporting-financial-reports` flag alone. Whether the entity is
 * actually ERP is verified server-side by the agent-side tools, so this element is only a
 * routing hint and a stale value cannot leak ERP-only data.
 */
export function useReportingErpModeAgentContext(): ApplicationContextElement[] {
  const isErpModeEnabled = useFeatureFlag(ldFlags.multiHierarchyCoaFinancialReports);
  const erpMode: ReportingErpMode = isErpModeEnabled ? 'erp' : 'non-erp';

  return useMemo(
    () => [
      {
        key: REPORTING_ERP_MODE_CONTEXT_KEY,
        label: 'Reporting ERP mode (erp or non-erp)',
        value: erpMode
      }
    ],
    [erpMode]
  );
}
